import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from 'lucide-react';
import { decodedToken } from '../../utils/auth';
import Swal from 'sweetalert2';
import { useGlobalState } from '../../utils/GlobalState';

const ProductInfoDetection = ({ productDetails }) => {
  const navigate = useNavigate();
  const { state, updateState } = useGlobalState();
  const [productType, setProductType] = useState("TV");
  const [modelName, setModelName] = useState("");
  const [hasBezel, setHasBezel] = useState(null);
  const [dimensions, setDimensions] = useState({
    width: { input: "", whole: 0, decimal: 0 },
    height: { input: "", whole: 0, decimal: 0 }
  });
  const [screenType, setScreenType] = useState(null);
  const [selectedInch, setSelectedInch] = useState("");

  const decoded = decodedToken();
  const companyInfo = decoded?.companyInfo || 'Unknown';
  const [brand, setBrand] = useState('');

  const defaultState = {
    productType: "TV",
    brand,
    modelName: "",
    hasBezel: null,
    leakageRation: null,
    dimensions: {
      width: { input: "", whole: 0, decimal: 0 },
      height: { input: "", whole: 0, decimal: 0 }
    },
    screenType: null,
    inchSize: ""
  };

  useEffect(() => {
   
    if (!state.formData) {
   
      updateState({ formData: [] });
    }
  }, [state.formData, updateState]);

  const latestFormData = state.formData && state.formData.length > 0 ? state.formData[state.formData.length - 1] : null;

  useEffect(() => {
  
    setProductType(latestFormData?.productType || defaultState.productType);
    setModelName(latestFormData?.modelName || defaultState.modelName);
    setHasBezel(
      latestFormData?.bezel === "yes" ? true : latestFormData?.bezel === "no" ? false : defaultState.hasBezel
    );
    setDimensions(
      latestFormData?.dimensions
        ? {
            width: {
              input: latestFormData.dimensions.width || "",
              whole: parseInt(latestFormData.dimensions.width?.split('.')[0]) || 0,
              decimal: parseInt(latestFormData.dimensions.width?.split('.')[1]?.[0]) || 0
            },
            height: {
              input: latestFormData.dimensions.height || "",
              whole: parseInt(latestFormData.dimensions.height?.split('.')[0]) || 0,
              decimal: parseInt(latestFormData.dimensions.height?.split('.')[1]?.[0]) || 0
            }
          }
        : defaultState.dimensions
    );
    setScreenType(latestFormData?.screenType || defaultState.screenType);
    setSelectedInch(latestFormData?.inchSize || defaultState.inchSize);
  }, [latestFormData]);

  useEffect(() => {
   
    if (productType === 'MNT') {
      setScreenType(null);
      setSelectedInch('');
      setDimensions(defaultState.dimensions);
    } else {
      setScreenType(null);
      setSelectedInch('');
      setDimensions(defaultState.dimensions);
    }
  }, [productType]);

  const handleDimensionChange = (dimension, value) => {
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      let whole = 0;
      let decimal = 0;
      if (value !== '') {
        const [w, d = '0'] = value.split('.');
        whole = parseInt(w) || 0;
        decimal = parseInt(d[0] || '0') || 0;
      }
      setDimensions(prev => ({
        ...prev,
        [dimension]: {
          input: value,
          whole,
          decimal
        }
      }));
      setSelectedInch('');
    }
  };

  const isFormValid = () => {
    if (!modelName || modelName.trim() === '') {
      return false;
    }
    const width = parseFloat(`${dimensions.width.whole}.${dimensions.width.decimal}`);
    const height = parseFloat(`${dimensions.height.whole}.${dimensions.height.decimal}`);
    if (isNaN(width) || width <= 0 || isNaN(height) || height <= 0) {
      return false;
    }
    if (productType === 'TV' || productType === 'MNT') {
      if (hasBezel === null) {
        return false;
      }
      if (productType === 'MNT' && screenType === null) {
        return false;
      }
    }
    return true;
  };

  const handleSave = () => {
    if (!isFormValid()) {
   
      Swal.fire({
        title: 'Invalid Input',
        text: 'Please fill all required fields correctly.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
      return;
    }

    const productData = {
      productType,
      brand,
      modelName,
      bezel: productType !== 'PD' ? (hasBezel ? 'yes' : 'no') : undefined,
      dimensions: {
        width: `${dimensions.width.whole}.${dimensions.width.decimal}`,
        height: `${dimensions.height.whole}.${dimensions.height.decimal}`
      },
      screenType: productType === 'MNT' ? screenType : undefined,
      inchSize: selectedInch || undefined,
      companyInfo
    };

    const updatedFormData = [...(state.formData || []), productData];
 
    updateState({ formData: updatedFormData });
    navigate('/upload-image-detection', { state: { productData } });
  };

  const handleBack = () => {

    updateState({ formData: [] });
    navigate("/AI-ODS");
  };

  return (
    <div className="min-h-screen bg-black text-white p-4">
      <div className="flex items-center mb-6">
        <button className="mr-4" onClick={handleBack}>
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-lg sm:text-xl font-medium whitespace-nowrap">Product Info Input</h1>
      </div>

      <div className="space-y-6">
        <div>
          <label className="block mb-3">Product Type</label>
          <div className="relative">
            <div className="flex w-full">
              {['TV', 'MNT', 'PD'].map((type) => (
                <button
                  key={type}
                  onClick={() => {
                    setProductType(type);
                  
                  }}
                  className={`flex-1 py-2 text-center relative ${
                    productType === type ? 'text-blue-500' : 'text-gray-400'
                  }`}
                >
                  {type}
                  {productType === type && (
                    <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-500" />
                  )}
                </button>
              ))}
            </div>
            <div className="absolute bottom-0 left-0 w-full h-px bg-gray-800" />
          </div>
        </div>
         <div>
          <label className="block mb-3">Brand</label>
          <input
            type="text"
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            placeholder="Enter brand name"
            className="w-full bg-gray-800 text-white rounded-lg px-4 py-3 placeholder-gray-400"
          />
        </div> 

        <div>
          <label className="block mb-3">Model Name</label>
          <input
            type="text"
            placeholder="Enter model name"
            value={modelName}
            onChange={(e) => {
              setModelName(e.target.value.slice(0, 25));
            
            }}
            maxLength={25}
            className="w-full bg-gray-800 rounded-lg px-4 py-3 text-white placeholder-gray-400"
          />
        </div>

        {productType !== 'PD' && (
          <div>
            <label className="block mb-3">Bezel</label>
            <div className="flex space-x-4">
              <button
                onClick={() => {
                  setHasBezel(true);
                
                }}
                className={`px-6 py-2 rounded-full ${
                  hasBezel === true ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400'
                }`}
              >
                Yes
              </button>
              <button
                onClick={() => {
                  setHasBezel(false);
               
                }}
                className={`px-6 py-2 rounded-full ${
                  hasBezel === false ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400'
                }`}
              >
                No
              </button>
            </div>
          </div>
        )}

        {(productType === 'TV' || productType === 'MNT') && (
          <div>
            {productType === 'MNT' && (
              <div className="mb-6">
                <label className="block mb-1">Screen Type</label>
                <div className="flex space-x-4">
                  <button
                    onClick={() => {
                      setScreenType(screenType === 'Flat' ? null : 'Flat');
               
                    }}
                    className={`px-6 py-2 rounded-full ${
                      screenType === 'Flat' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400'
                    }`}
                  >
                    Flat
                  </button>
                  <button
                    onClick={() => {
                      setScreenType(screenType === 'Curved' ? null : 'Curved');
                  
                    }}
                    className={`px-6 py-2 rounded-full ${
                      screenType === 'Curved' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400'
                    }`}
                  >
                    Curved
                  </button>
                </div>
              </div>
            )}
            <div className="mb-6">
              <label className="block mb-2">Inch Size Selection</label>
              <div className="relative">
                <select
                  className="dropdown block w-full bg-gray-800 text-white rounded-lg px-4 py-3"
                  onChange={(e) => {
                    const selectedInch = e.target.value;
                    const inchToDimensions =
                      productType === 'TV'
                      ? {
                        '32': { width: '708', height: '398' },
                        '40': { width: '885', height: '498' },
                        '42': { width: '933', height: '535' },
                        '43': { width: '952', height: '535' },
                        '48': { width: '1069', height: '612' },
                        '50': { width: '1107', height: '623' },
                        '55': { width: '1218', height: '685' },
                        '58': { width: '1285', height: '723' },
                        '65': { width: '1439', height: '810' },
                        '70': { width: '1562', height: '860' },
                        '75': { width: '1660', height: '934' },
                        '77': { width: '1721', height: '985' },
                        '85': { width: '1882', height: '1058' },
                        '86': { width: '1905', height: '1072' },
                        '98': { width: '2170', height: '1220' },
                        '100': { width: '2214', height: '1246' },
                      }
                    : {
                        '16': screenType === 'Flat' ? { width: '364', height: '230' } : { width: '', height: '' },
                        '22': screenType === 'Flat' ? { width: '492', height: '290' } : { width: '', height: '' },
                        '24': screenType === 'Flat' ? { width: '543', height: '320' } : { width: '537', height: '323' },
                        '25': screenType === 'Flat' ? { width: '560', height: '324' } : { width: '', height: '' },
                        '27': screenType === 'Flat' ? { width: '614', height: '368' } : { width: '612', height: '368' },
                        '30': screenType === 'Flat' ? { width: '', height: '' } : { width: '701', height: '322' },
                        '32': screenType === 'Flat' ? { width: '715', height: '421' } : { width: '700', height: '424' },
                        '34': screenType === 'Flat' ? { width: '817', height: '371' } : { width: '810', height: '366' },
                        '45': screenType === 'Flat' ? { width: '', height: '' } : { width: '1085', height: '337' },
                        '49': screenType === 'Flat' ? { width: '', height: '' } : { width: '1194', height: '370' },
                      };
                    if (selectedInch) {
                      const { width, height } = inchToDimensions[selectedInch] || { width: '', height: '' };
                      setDimensions({
                        width: {
                          input: width,
                          whole: width ? parseInt(width) : 0,
                          decimal: width && width.includes('.') ? parseInt(width.split('.')[1][0] || '0') : 0,
                        },
                        height: {
                          input: height,
                          whole: height ? parseInt(height) : 0,
                          decimal: height && height.includes('.') ? parseInt(height.split('.')[1][0] || '0') : 0,
                        },
                      });
                    } else {
                      setDimensions({
                        width: { input: '', whole: 0, decimal: 0 },
                        height: { input: '', whole: 0, decimal: 0 },
                      });
                    }
                    setSelectedInch(selectedInch);
                   
                  }}
                  value={selectedInch}
                  disabled={productType === 'MNT' && !screenType}
                >
                  <option value="">Select inch size</option>
                  {productType === 'TV' && (
                    <>
                      <option value="32">32 inches</option>
                      <option value="40">40 inches</option>
                      <option value="42">42 inches</option>
                      <option value="43">43 inches</option>
                      <option value="48">48 inches</option>
                      <option value="50">50 inches</option>
                      <option value="55">55 inches</option>
                      <option value="58">58 inches</option>
                      <option value="65">65 inches</option>
                      <option value="70">70 inches</option>
                      <option value="75">75 inches</option>
                      <option value="77">77 inches</option>
                      <option value="85">85 inches</option>
                      <option value="86">86 inches</option>
                      <option value="98">98 inches</option>
                      <option value="100">100 inches</option>
                    </>
                  )}
                  {productType === 'MNT' && (
                    <>
                      <option value="16">16 inches</option>
                      <option value="22">22 inches</option>
                      <option value="24">24 inches</option>
                      <option value="25">25 inches</option>
                      <option value="27">27 inches</option>
                      <option value="30">30 inches</option>
                      <option value="32">32 inches</option>
                      <option value="34">34 inches</option>
                      <option value="45">45 inches</option>
                      <option value="49">49 inches</option>
                    </>
                  )}
                </select>
              </div>
            </div>
          </div>
        )}

        {['width', 'height'].map((dimension) => (
          <div key={dimension}>
            <label className="block mb-3 capitalize">
              {dimension}: {' '}
              <span className="text-gray-400">
                {dimensions[dimension].input ? `${dimensions[dimension].whole}.${dimensions[dimension].decimal} mm` : 'N/A'}
              </span>
            </label>
            <div className="flex items-center space-x-4">
              <input
                type="text"
                value={dimensions[dimension].input}
                onChange={(e) => handleDimensionChange(dimension, e.target.value)}
                placeholder="Manually Input"
                className="bg-gray-800 rounded-lg px-4 py-3 text-white placeholder-gray-400 w-40"
              />
            </div>
          </div>
        ))}

        <div className="flex space-x-4">
          <button 
            className={`px-6 py-3 rounded-lg mt-6 text-white w-full ${
              !isFormValid()
                ? "bg-gray-600 cursor-not-allowed" 
                : "bg-blue-600 hover:bg-blue-700"
            }`}
            onClick={handleSave}
            disabled={!isFormValid()}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductInfoDetection;